import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/Button';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import {
  useQariMutations,
  useQariProfile,
} from '@/hooks/useQariProfile';
import { QariStatusBadge } from '@/components/qari/QariStatusBadge';
import {
  QARI_EDITABLE_STATUSES,
  type QariProfile,
  type QariStatus,
} from '@/types/qari';

/**
 * /qari — Authenticated landing for the Qari module.
 *
 * Branches by current profile status:
 *  - error                      → calm error block + Retry
 *  - no row yet                 → CTA to start onboarding
 *  - draft / rejected           → Continue / Revise CTA
 *  - pending_review             → Calm "we're reviewing" + withdraw option
 *  - under_verification         → Same calm message, no withdraw
 *  - approved                   → "Public profile is live" stub
 *  - suspended / inactive       → Status-only, contact support copy
 */
export function QariDashboard() {
  useDocumentTitle('Qari');

  const profile = useQariProfile();
  const { withdrawSubmission } = useQariMutations();

  if (profile.isLoading) {
    return (
      <section className="container-prose py-16">
        <p className="text-ink-500 dark:text-ink-400 text-sm">Loading…</p>
      </section>
    );
  }

  if (profile.isError) {
    return (
      <section className="container-prose py-16">
        <p className="text-xs uppercase tracking-[0.22em] text-rose-600 dark:text-rose-400 mb-3">
          Qari
        </p>
        <h1 className="font-serif text-display-2 text-ink-900 dark:text-ink-50">
          Couldn't load your profile
        </h1>
        <p className="text-base text-ink-600 dark:text-ink-300 leading-relaxed mt-3 max-w-prose">
          {profile.error instanceof Error
            ? profile.error.message
            : 'Unknown error.'}
        </p>
        <div className="mt-6">
          <Button variant="secondary" onClick={() => profile.refetch()}>
            Try again
          </Button>
        </div>
      </section>
    );
  }

  if (!profile.data) {
    return (
      <section className="container-prose pt-12 sm:pt-16 pb-16">
        <Header subtitle="Qari" title="Become a qari on Alhudah" />
        <p className="text-base text-ink-600 dark:text-ink-300 leading-relaxed mt-4 max-w-prose">
          Set up your public profile in a few short steps. We'll review
          your details and verification documents before listing your
          profile.
        </p>
        <div className="mt-8">
          <Link to="/qari/onboarding">
            <Button variant="primary" size="lg">
              Start onboarding
            </Button>
          </Link>
        </div>
      </section>
    );
  }

  return (
    <section className="container-prose pt-12 sm:pt-16 pb-16">
      <Header subtitle="Qari" title="Your qari profile" />
      <div className="mt-4 flex items-center gap-3">
        <QariStatusBadge status={profile.data.status} />
        <span className="text-sm text-ink-500 dark:text-ink-400">
          Last updated{' '}
          {new Date(profile.data.updated_at).toLocaleDateString()}
        </span>
      </div>

      <StatusBody profile={profile.data} />

      <div className="mt-8 flex flex-wrap gap-3">
        {QARI_EDITABLE_STATUSES.has(profile.data.status) && (
          <Link to="/qari/onboarding">
            <Button variant="primary">
              {profile.data.status === 'rejected'
                ? 'Revise & resubmit'
                : 'Continue editing'}
            </Button>
          </Link>
        )}
        {profile.data.status === 'pending_review' && (
          <Button
            variant="secondary"
            onClick={() => withdrawSubmission.mutate()}
            disabled={withdrawSubmission.isPending}
          >
            {withdrawSubmission.isPending
              ? 'Withdrawing…'
              : 'Withdraw submission'}
          </Button>
        )}
      </div>
    </section>
  );
}

function Header({
  subtitle,
  title,
}: {
  subtitle: string;
  title: string;
}) {
  return (
    <header>
      <p className="text-xs uppercase tracking-[0.22em] text-accent-600 dark:text-accent-400 mb-3">
        {subtitle}
      </p>
      <h1 className="font-serif text-display-2 text-ink-900 dark:text-ink-50">
        {title}
      </h1>
    </header>
  );
}

const COPY: Record<QariStatus, string> = {
  draft:
    "Your draft is saved. Pick up where you left off and submit it for review when you're ready.",
  pending_review:
    "We've received your profile. A reviewer will look it over shortly. You can withdraw or edit while it's pending.",
  under_verification:
    "A reviewer is verifying your documents. You'll see a status change here once they're done.",
  approved:
    'Your profile is live. Students can find you in the Alhudah qari directory.',
  rejected:
    "Some details need attention before we can list your profile. See the note below, make changes, and resubmit.",
  suspended:
    'Your profile is currently suspended. Reach out to the Alhudah team if you believe this is in error.',
  inactive:
    "Your profile is inactive. Edit it to set things up again, or contact support if you'd like help.",
};

function StatusBody({ profile }: { profile: QariProfile }) {
  const text = COPY[profile.status];
  return (
    <div className="mt-6 max-w-prose">
      <p className="text-base text-ink-700 dark:text-ink-200 leading-relaxed">
        {text}
      </p>
      {profile.status === 'rejected' && profile.rejection_reason && (
        <div className="mt-4 rounded-lg border border-rose-200/70 dark:border-rose-900/40 bg-rose-50/60 dark:bg-rose-950/30 p-4">
          <p className="text-xs uppercase tracking-[0.18em] text-rose-700 dark:text-rose-300 mb-2">
            Reviewer note
          </p>
          <p className="text-sm text-rose-900 dark:text-rose-100 leading-relaxed whitespace-pre-wrap">
            {profile.rejection_reason}
          </p>
        </div>
      )}
    </div>
  );
}
